import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Calendar, Clock, Search, ArrowRight } from "lucide-react";
import { useState, useMemo, JSX } from "react";
import { blogPosts, blogCategories } from "@/data";
import { formatDate, cn } from "@/lib/utils";

export const Route = createFileRoute("/blog/")({
  component: BlogPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function BlogPage(): JSX.Element {
  const [activeCategory, setActiveCategory] = useState("All Posts");
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesCategory =
        activeCategory === "All Posts" || post.category === activeCategory;
      const matchesSearch =
        !searchQuery ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some((t) =>
          t.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const featured = blogPosts.filter((p) => p.featured)[0];

  return (
    <>
      <Helmet>
        <title>Tax Insights & Blog | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Expert insights on Kenyan tax law, KRA compliance, eTIMS, PAYE, and international tax. Stay informed with Rosewood's tax blog."
        />
      </Helmet>

      <section className="bg-[#FAF7F2] pt-16 pb-14">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >

            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-4">
              Blog & <span className=" text-[#6B1E2A]">Insights</span>
            </h1>
            <p className="text-lg text-[#6B6B6B]">
              Expert analysis, compliance guides, and the latest Kenya tax news
              from our team.
            </p>
          </motion.div>
        </div>
      </section>

      {featured && (
        <section className="py-10 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <Link to={`/blog/${featured.slug}` as never} className="group">
              <div className="grid lg:grid-cols-2 gap-10 items-center bg-[#FAF7F2] rounded-2xl overflow-hidden hover:shadow-xs transition-shadow">
                <div className="aspect-[4/3] bg-[#6B1E2A]/10 flex items-center justify-center relative">

                </div>
                <div className="p-8 lg:p-10">

                  <h2 className="font-serif text-2xl md:text-3xl font-semibold text-[#1A1A1A] mt-4 mb-4 leading-tight group-hover:text-[#6B1E2A] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-[#6B6B6B] leading-relaxed mb-6 line-clamp-3">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center gap-6 mb-6 text-sm text-[#6B6B6B]">
                    <span className="flex items-center gap-1.5">
                      <Calendar size={13} />
                      {formatDate(featured.publishedAt)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock size={13} />
                      {featured.readTime} min read
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-[#6B1E2A] rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {featured.author.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#1A1A1A]">
                        {featured.author.name}
                      </span>
                    </div>
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-[#6B1E2A] group-hover:text-[#4A1219]">
                      Read Article{" "}
                      <ArrowRight
                        size={14}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">

          <div className="flex flex-col md:flex-row gap-4 mb-10">
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B6B]"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-11 pr-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#6B1E2A]/40 transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {blogCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                    activeCategory === cat
                      ? "bg-[#6B1E2A] text-white"
                      : "bg-[#FAF7F2] text-[#6B6B6B] hover:bg-[#6B1E2A]/10 hover:text-[#6B1E2A]",
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#6B6B6B]">
                No articles found matching your search.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, i) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <Link
                    to={`/blog/${post.slug}` as never}
                    className="group block h-full"
                  >
                    <div className="h-full bg-[#FAF7F2] rounded-xl overflow-hidden hover:shadow-xs hover:-translate-y-0.5 transition-all flex flex-col">

                      <div className="aspect-[16/9] bg-[#6B1E2A]/8 relative flex items-center justify-center">
 
                      </div>

                      <div className="p-5 flex-1 flex flex-col">
                        <div className="flex items-center gap-4 text-xs text-[#6B6B6B] mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar size={11} />
                            {formatDate(post.publishedAt)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={11} />
                            {post.readTime} min
                          </span>
                        </div>
                        <h3 className="font-semibold text-[#1A1A1A] mb-2 leading-snug group-hover:text-[#6B1E2A] transition-colors line-clamp-2 flex-1">
                          {post.title}
                        </h3>
                        <p className="text-sm text-[#6B6B6B] line-clamp-2 mb-4">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center gap-2 mt-auto">
                          <div className="w-7 h-7 bg-[#6B1E2A]/10 rounded-full flex items-center justify-center">
                            <span className="text-[#6B1E2A] text-[11px] font-bold">
                              {post.author.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </div>
                          <span className="text-xs font-medium text-[#1A1A1A]">
                            {post.author.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
